/// <reference types="vite/client" />

import { isQueryFlagEnabled } from "@/utils"
import "../evp-html-embed"
import "../evp-mini-app"
import "../evp-qr-scannable"
import "../explore/homeBlockClickTracking"
import { registerIslandLoader } from "../evp-react-island"
import "../evp-support-toc"
import "../codemirror"

// When a deploy rotates the hashed chunk filenames, a page that's been open
// (or aggressively cached, as in the Android app WebView) can try to lazily
// import a chunk URL that no longer exists on the CDN, producing
// "Failed to fetch dynamically imported module". Reloading once pulls a fresh
// entry that points at valid chunk URLs. The timestamp guard ensures we only
// reload once per short window, so a chunk that's genuinely gone (or an offline
// device) falls through to the island error boundary instead of looping.
const PRELOAD_RELOAD_KEY = "vite-preload-reloaded-at"
const PRELOAD_RELOAD_PARAM = "__vite_reload"
const PRELOAD_RELOAD_WINDOW_MS = 10_000

window.addEventListener("vite:preloadError", (event) => {
  const now = Date.now()

  let lastReloadedAt = 0
  try {
    lastReloadedAt = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0
  } catch {
    // sessionStorage can throw in some WebView/privacy contexts; treat as 0.
  }

  if (now - lastReloadedAt < PRELOAD_RELOAD_WINDOW_MS) {
    // Already reloaded recently and still failing — let it surface.
    return
  }

  try {
    // Persist the timestamp *before* reloading so the throttle survives the
    // reload. If sessionStorage is unavailable (some WebView/privacy contexts),
    // this throws — skip the reload and let the error surface rather than
    // looping forever on a guard we can never write.
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(now))

    // A plain reload re-fetches the SAME cached HTML, which still points at the
    // purged chunk — so the failure repeats. Adding a unique query param changes
    // the browser/WebView HTTP cache key, forcing a fresh HTML document that
    // references the current entry (and thus existing chunk URLs). replace()
    // avoids leaving a junk entry in the back/forward history.
    const url = new URL(window.location.href)
    url.searchParams.set(PRELOAD_RELOAD_PARAM, String(now))

    event.preventDefault()
    window.location.replace(url.toString())
  } catch {
    // URL/sessionStorage can throw in some WebView/privacy contexts; let the
    // error surface rather than risk looping on a reload we can't guard.
  }
})

// Strip the cache-bust param from the address bar after a successful recovery so
// it doesn't linger on shared/bookmarked URLs. Cosmetic only — safe to skip if
// any of these APIs are unavailable.
try {
  const currentUrl = new URL(window.location.href)
  if (currentUrl.searchParams.has(PRELOAD_RELOAD_PARAM)) {
    currentUrl.searchParams.delete(PRELOAD_RELOAD_PARAM)
    window.history.replaceState(null, "", currentUrl.toString())
  }
} catch {
  // No-op: cosmetic cleanup only.
}

for (const [path, loader] of Object.entries(
  import.meta.glob("../**/*.island.tsx"),
)) {
  const name = path.replace("../", "").replace(".island.tsx", "")
  registerIslandLoader(name, loader)
}

if (
  isQueryFlagEnabled("theme_editor") &&
  !document.getElementById("evp-theme-editor")
) {
  const div = document.createElement("div")
  div.id = "evp-theme-editor"
  document.body.appendChild(div)
  div.innerHTML = `<evp-react-island component="ui-kit/ThemeEditor"></evp-react-island>`
}
